// Reading the `quota_exceeded` throw back off the wire, and saying when the
// reader can go again.
//
// `ConvexError.data` is `unknown` by construction, so this is one of the spots
// the type system has already stopped helping — a shape check, not a cast. The
// caller renders a subscribe prompt from what comes back, and a wrong `retryAt`
// would tell a reader to come back at the wrong time, so the phrasing is here
// (pure, tested) rather than inline in the component.

import { ConvexError } from "convex/values";

/** Which cap refused, when it lifts, and what it was. */
export interface QuotaRefusal {
  scope: "hour" | "day";
  /** Epoch ms — the instant the refused call would be allowed. */
  retryAt: number;
  limit: number;
}

/** The `quota_exceeded` refusal carried by `error`, or `null` for anything
 *  else (including the auth errors, which have their own handling). */
export function readQuotaError(error: unknown): QuotaRefusal | null {
  if (!(error instanceof ConvexError)) return null;
  const data: unknown = error.data;
  if (typeof data !== "object" || data === null) return null;
  const { code, scope, retryAt, limit } = data as Record<string, unknown>;
  if (code !== "quota_exceeded") return null;
  if (scope !== "hour" && scope !== "day") return null;
  if (typeof retryAt !== "number" || !Number.isFinite(retryAt)) return null;
  if (typeof limit !== "number" || !Number.isFinite(limit)) return null;
  return { scope, retryAt, limit };
}

/** Which axis, if any, the current quota is exhausted on — day checked before
 *  hour, mirroring `decideTranslationSpend`'s precedence in
 *  `convex/model/translationQuota.ts`. `null` on an unmetered axis (`limits`
 *  entry `null`), even if `remaining` also reads zero there. */
export function limitReachedScope(
  quota:
    | {
        remaining: { hour: number | null; day: number | null };
        limits: { perHour: number | null; perDay: number | null };
      }
    | null
    | undefined,
): "hour" | "day" | null {
  if (!quota) return null;
  if (quota.limits.perDay !== null && quota.remaining.day === 0) return "day";
  if (quota.limits.perHour !== null && quota.remaining.hour === 0)
    return "hour";
  return null;
}

const MINUTE_MS = 60 * 1000;

/**
 * When the reader can look something up again, in words.
 *
 * Rounds UP to the next whole minute, deliberately: "in 1 minute" that turns
 * out to be 90 seconds reads as a broken promise, while an extra few seconds
 * of patience never does. Under a minute is "in a moment" rather than a
 * second count — nobody wants to watch that number.
 */
export function retryPhrase(retryAt: number, now: number): string {
  const ms = retryAt - now;
  if (ms <= 0) return "now";
  if (ms < MINUTE_MS) return "in a moment";
  const minutes = Math.ceil(ms / MINUTE_MS);
  if (minutes < 60) {
    return `in ${String(minutes)} minute${minutes === 1 ? "" : "s"}`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24)
    return `in about ${String(hours)} hour${hours === 1 ? "" : "s"}`;
  return "tomorrow";
}

/** The clock time the allowance returns, for the reader who would rather see
 *  an actual time than a countdown. Local to their device, 24h-agnostic. */
export function retryClock(retryAt: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(retryAt));
}
